import { deleteToken, getToken, onMessage } from 'firebase/messaging';
import { auth, getFirebaseMessaging } from '../firebase';
import { callMessagingFunction } from '../messaging/callableMessagingClient';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';
const SERVICE_WORKER_READY_TIMEOUT_MS = 3500;
const PUSH_TOKEN_TIMEOUT_MS = 7000;

const withTimeout = (promise, timeoutMs, status) =>
  Promise.race([
    promise,
    new Promise((resolve) => {
      window.setTimeout(() => resolve({ timedOut: true, status }), timeoutMs);
    }),
  ]);

const getServiceWorkerRegistration = async () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const result = await withTimeout(
      navigator.serviceWorker.ready,
      SERVICE_WORKER_READY_TIMEOUT_MS,
      'service-worker-timeout'
    );
    return result?.timedOut ? null : result;
  } catch {
    return null;
  }
};

export const canUsePushNotifications = () =>
  typeof window !== 'undefined'
  && 'Notification' in window
  && typeof navigator !== 'undefined'
  && 'serviceWorker' in navigator;

export const getPushNotificationPermission = () => {
  if (!canUsePushNotifications()) {
    return 'unsupported';
  }

  return window.Notification.permission;
};

export const isPushNotificationConfigured = () => Boolean(VAPID_KEY);

export const registerCurrentDeviceForPush = async ({
  prompt = false,
  source = 'chat',
} = {}) => {
  if (!canUsePushNotifications()) {
    return { success: false, status: 'unsupported' };
  }

  const user = auth.currentUser;
  if (!user) {
    return { success: false, status: 'unauthenticated' };
  }

  let permission = window.Notification.permission;
  if (permission === 'default' && prompt) {
    permission = await window.Notification.requestPermission();
  }

  if (permission !== 'granted') {
    return { success: false, status: permission, permission };
  }

  if (!VAPID_KEY) {
    return { success: false, status: 'missing-vapid-key' };
  }

  let messaging = null;
  try {
    messaging = await getFirebaseMessaging();
    if (!messaging) {
      return { success: false, status: 'unsupported' };
    }
  } catch (error) {
    return { success: false, status: 'messaging-init-failed', error };
  }

  const serviceWorkerRegistration = await getServiceWorkerRegistration();
  let tokenResult = null;
  try {
    tokenResult = await withTimeout(
      getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: serviceWorkerRegistration || undefined,
      }),
      PUSH_TOKEN_TIMEOUT_MS,
      'push-token-timeout'
    );
  } catch (error) {
    return { success: false, status: 'push-token-failed', error };
  }

  if (tokenResult?.timedOut) {
    return { success: false, status: tokenResult.status };
  }

  const token = tokenResult;

  if (!token) {
    return { success: false, status: 'no-token' };
  }

  try {
    await callMessagingFunction('registerNotificationToken', {
      token,
      platform: 'web',
      source,
      userAgent: window.navigator.userAgent || '',
    });
  } catch (error) {
    return { success: false, status: 'token-register-failed', error };
  }

  return { success: true, status: 'registered', token };
};

export const unregisterCurrentDeviceForPush = async () => {
  if (!canUsePushNotifications() || !auth.currentUser || !VAPID_KEY) {
    return { success: false, status: 'not-registered' };
  }

  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    return { success: false, status: 'unsupported' };
  }

  const serviceWorkerRegistration = await getServiceWorkerRegistration();
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: serviceWorkerRegistration || undefined,
  });

  if (token) {
    await callMessagingFunction('unregisterNotificationToken', { token });
  }

  await deleteToken(messaging);
  return { success: true, status: 'unregistered' };
};

export const listenForForegroundPushMessages = async (onPushMessage) => {
  if (!canUsePushNotifications()) {
    return null;
  }

  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    return null;
  }

  return onMessage(messaging, onPushMessage);
};
